import { useState, useEffect } from "react";
import { Truck } from "../types";

interface UseVehicleDataProps {
  trucks: Truck[];
  availabilityFilter?: "all" | "available" | "unavailable";
  sortBy: "time" | "alphabetical";
  searchTerm: string;
}

export const useVehicleData = ({
  trucks,
  availabilityFilter = "all",
  sortBy,
  searchTerm,
}: UseVehicleDataProps) => {
  const [groupedTrucks, setGroupedTrucks] = useState<Record<string, Truck[]>>(
    {}
  );

  useEffect(() => {
    const groupVehicles = () => {
      const grouped: Record<string, Truck[]> = {};

      // Group trucks by registration
      trucks.forEach((truck) => {
        if (!grouped[truck.registration]) {
          grouped[truck.registration] = [];
        }
        grouped[truck.registration].push(truck);
      });

      // Sort trucks within each group by time (newest first)
      Object.keys(grouped).forEach((reg) => {
        grouped[reg].sort(
          (a, b) => new Date(b.intime).getTime() - new Date(a.intime).getTime()
        );
      });

      // Apply availability filter
      let filteredGrouped = grouped;
      if (availabilityFilter !== "all") {
        filteredGrouped = {};
        Object.entries(grouped).forEach(([reg, trucks]) => {
          const latestTruck = trucks[0];
          const lastDirection = latestTruck.direction?.toLowerCase() || "";
          const isIn = lastDirection === "in";

          if (availabilityFilter === "unavailable" && isIn) {
            filteredGrouped[reg] = trucks;
          } else if (
            availabilityFilter === "available" &&
            lastDirection === "out"
          ) {
            filteredGrouped[reg] = trucks;
          }
        });
      }

      // Search filter for license plates
      if (searchTerm.trim()) {
        const searchFiltered: Record<string, Truck[]> = {};
        Object.entries(filteredGrouped).forEach(([reg, trucks]) => {
          if (reg.toLowerCase().startsWith(searchTerm.toLowerCase())) {
            searchFiltered[reg] = trucks;
          }
        });
        filteredGrouped = searchFiltered;
      }

      // Sort groups (alphabetical or by time)
      const sortedEntries = Object.entries(filteredGrouped).sort(
        ([regA, trucksA], [regB, trucksB]) => {
          if (sortBy === "alphabetical") {
            return regA.localeCompare(regB);
          } else {
            const newestTimeA = new Date(trucksA[0].intime).getTime();
            const newestTimeB = new Date(trucksB[0].intime).getTime();
            return newestTimeB - newestTimeA;
          }
        }
      );

      const sortedGrouped: Record<string, Truck[]> = {};
      sortedEntries.forEach(([reg, trucks]) => {
        sortedGrouped[reg] = trucks;
      });

      return sortedGrouped;
    };

    setGroupedTrucks(groupVehicles());
  }, [trucks, sortBy, availabilityFilter, searchTerm]);

  return groupedTrucks;
};
